
import { AsanaTask, ExtractedData, TaskTemplate, TaskPackage } from "../types";
import { loadTemplatesFromCloud, saveTemplatesToCloud, getAuthToken } from "./firebaseService";

const STORAGE_KEY = 'myesalon_task_templates';

// All Asana calls go through our authenticated backend proxy, which holds the
// Asana PAT server-side. The token is never exposed to the browser.
const apiPost = async (path: string, body: unknown): Promise<any> => {
  const token = await getAuthToken();
  const response = await fetch(path, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body ?? {}),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Request to ${path} failed (${response.status}).`);
  }
  return response.json();
};

const cleanVal = (val: any) => {
  if (val === 'null' || val === null || val === undefined) return '';
  return String(val);
};

const stateMap: { [key: string]: string } = {
  "alabama": "al", "alaska": "ak", "arizona": "az", "arkansas": "ar", "california": "ca", 
  "colorado": "co", "connecticut": "ct", "delaware": "de", "florida": "fl", "georgia": "ga", 
  "hawaii": "hi", "idaho": "id", "illinois": "il", "indiana": "in", "iowa": "ia", 
  "kansas": "ks", "kentucky": "ky", "louisiana": "la", "maine": "me", "maryland": "md", 
  "massachusetts": "ma", "michigan": "mi", "minnesota": "mn", "mississippi": "ms", "missouri": "mo", 
  "montana": "mt", "nebraska": "ne", "nevada": "nv", "new hampshire": "nh", "new jersey": "nj", 
  "new mexico": "nm", "new york": "ny", "north carolina": "nc", "north dakota": "nd", "ohio": "oh", 
  "oklahoma": "ok", "oregon": "or", "pennsylvania": "pa", "rhode island": "ri", "south carolina": "sc", 
  "south dakota": "sd", "tennessee": "tn", "texas": "tx", "utah": "ut", "vermont": "vt", 
  "virginia": "va", "washington": "wa", "west virginia": "wv", "wisconsin": "wi", "wyoming": "wy", 
  "district of columbia": "dc"
};

export const generateCredentials = (data: ExtractedData) => {
  const clean = (str: string) => (str || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  const fullState = (data.businessInfo.state || '').toLowerCase().trim();
  const stateAbbr = stateMap[fullState] || fullState;

  const user = `${clean(data.businessInfo.name)}${clean(stateAbbr)}`;
  const randomSuffix = Math.floor(10 + (data.businessInfo.name.length * 7) % 89); 
  const pass = `${user}${randomSuffix}`;
  return { username: user, password: pass };
};

export const DEFAULT_TEMPLATES: TaskTemplate[] = [
  { id: '1', name: "New Website Agreement", notes: "Ensure the agreement is signed and stored for {businessname}.", packages: [TaskPackage.PREMIUM, TaskPackage.ELITE, TaskPackage.SUPREME] },
  { id: '2', name: "Step 1-11 - AGENT {agentname}", notes: "Agent assigned: {agentname}", packages: [TaskPackage.PREMIUM, TaskPackage.ELITE, TaskPackage.SUPREME] },
  { id: '3', name: "DOMAIN + Follow-up Missing Info", notes: "Website: {website}. Check domain availability. Creds: {username} / {password}", packages: [TaskPackage.PREMIUM, TaskPackage.ELITE, TaskPackage.SUPREME] },
  { id: '4', name: "GG Auto Respond", notes: "Set up auto-responses for {businessname} in GoHighLevel.", packages: [TaskPackage.PREMIUM, TaskPackage.ELITE, TaskPackage.SUPREME] },
  { id: '5', name: "QC email and text", notes: "Verify Business Email: {businessemail}\nVerify Owner Email: {owneremail}", packages: [TaskPackage.PREMIUM, TaskPackage.ELITE, TaskPackage.SUPREME] },
  { id: '6', name: "Text Owner ELITE Package", notes: "Hi anh/chị, Em bên Công Ty MyESALON. Team em làm việc chung with {agentname} để tạo website cho anh/chị. Xin anh/chị vui lòng text for team website xin các thông tin sau để team tiến hành build website cho tiệm {businessname} ở {city} nhé:\n\n-Đây là domain của tiệm, anh chị giúp team approve để team tiến hành mua nha\n-2 màu chủ đạo anh chị muốn cho website\n-Tiệm chuẩn bị mở hay đã hoạt động. Nếu chuẩn bị mở thì cho team xin ngày Soft Opening và Grand Opening", packages: [TaskPackage.PREMIUM, TaskPackage.ELITE, TaskPackage.SUPREME] },
  { id: '7', name: "Text Owner", notes: "Send welcome text to {ownername} at {ownerphone}.", packages: [TaskPackage.PREMIUM, TaskPackage.ELITE, TaskPackage.SUPREME] },
  { id: '8', name: "DROPBOX", notes: "Create folder for {businessname} at {address}, {city}.", packages: [TaskPackage.PREMIUM, TaskPackage.ELITE, TaskPackage.SUPREME] },
  { id: '9', name: "Owner Info", notes: "Full Name: {ownername}\nFirst: {ownerfirstname}\nLast: {ownerlastname}\nPhone: {ownerphone}\nEmail: {owneremail}", packages: [TaskPackage.PREMIUM, TaskPackage.ELITE, TaskPackage.SUPREME] },
  { id: '10', name: "Business Info", notes: "Business: {businessname}\nPhone: {businessphone}\nAddress: {address}, {city}, {state} {zip}", packages: [TaskPackage.PREMIUM, TaskPackage.ELITE, TaskPackage.SUPREME] },
  { id: '11', name: "Website Content (SEO Starter Kit)", notes: "{seocontent}", packages: [TaskPackage.PREMIUM, TaskPackage.ELITE, TaskPackage.SUPREME] },
  { id: '12', name: "Invoice", notes: "Generate and send initial invoice to {ownername}. Attached is the original document.", packages: [TaskPackage.PREMIUM, TaskPackage.ELITE, TaskPackage.SUPREME] },
];

export const fetchTemplates = async (): Promise<TaskTemplate[]> => {
  try {
    const cloudTemplates = await loadTemplatesFromCloud();
    if (cloudTemplates) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(cloudTemplates));
      return cloudTemplates;
    }
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    }
  } catch (e) {
    console.error("Failed to load templates", e);
  }
  return DEFAULT_TEMPLATES;
};

export const updateTemplates = async (templates: TaskTemplate[]) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(templates));
  try {
    await saveTemplatesToCloud(templates);
  } catch (e) {
    console.error("Cloud save failed", e);
  }
  window.dispatchEvent(new CustomEvent('myesalon:templates-updated'));
};

export const processShortcodes = (text: string, data: ExtractedData): string => {
  if (!text) return "";
  const creds = generateCredentials(data);
  
  let seoBlock = "No SEO content generated.";
  if (data.seoContent) {
    const locationParts = [data.businessInfo.city, data.businessInfo.state].filter(Boolean);
    const location = locationParts.length ? ` (${locationParts.join(', ')})` : '';
    const businessName = data.businessInfo.name || 'this business';

    const titlesText = data.seoContent.titles
      .map((t, i) => `${i + 1}. ${t}`)
      .join('\n');

    const sectionsText = data.seoContent.sections
      .map((s, i) => `${i + 1}/ ${s.title}\n${s.content}`)
      .join('\n\n');

    seoBlock = `
Clickbait Titles for ${businessName}${location}

${titlesText}


Professional & Catchy Paragraphs

${sectionsText}
    `.trim();
  }

  const mapping: Record<string, string> = {
    '{businessname}': cleanVal(data.businessInfo.name),
    '{businesstype}': cleanVal(data.businessType),
    '{address}': cleanVal(data.businessInfo.address),
    '{city}': cleanVal(data.businessInfo.city),
    '{state}': cleanVal(data.businessInfo.state),
    '{zip}': cleanVal(data.businessInfo.zip),
    '{businessphone}': cleanVal(data.businessInfo.phone),
    '{businessemail}': cleanVal(data.businessInfo.email),
    '{website}': cleanVal(data.businessInfo.website) || 'None',
    '{ownername}': cleanVal(data.ownerInfo.name),
    '{ownerfirstname}': cleanVal(data.ownerInfo.firstName),
    '{ownerlastname}': cleanVal(data.ownerInfo.lastName),
    '{ownerphone}': cleanVal(data.ownerInfo.phone),
    '{owneremail}': cleanVal(data.ownerInfo.email),
    '{agentname}': cleanVal(data.agentName || 'Unknown').toUpperCase(),
    '{username}': creds.username,
    '{password}': creds.password,
    '{seocontent}': seoBlock
  };

  let processed = text;
  Object.entries(mapping).forEach(([code, value]) => {
    const escapedCode = code.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(escapedCode, 'gi');
    processed = processed.replace(regex, value);
  });
  
  return processed;
};

export const getWorkspaces = async () => {
  return apiPost("/api/asana/workspaces", {});
};

export const createProject = async (workspaceId: string, data: ExtractedData) => {
  return apiPost("/api/asana/project", {
    workspaceId,
    project: {
      name: data.businessInfo.name,
      city: data.businessInfo.city,
      state: data.businessInfo.state,
      businessType: data.businessType,
    },
  });
};

export const createTasks = async (
  projectId: string,
  tasks: AsanaTask[]
): Promise<{ gid: string; name: string }[]> => {
  return apiPost("/api/asana/tasks", { projectId, tasks });
};

export const uploadAttachment = async (
  taskGid: string,
  base64Data: string,
  fileName: string
) => {
  return apiPost("/api/asana/attachment", { taskGid, base64Data, fileName });
};

export const generateTasksFromTemplates = (data: ExtractedData, templates: TaskTemplate[]): AsanaTask[] => {
  const filteredTemplates = data.package 
    ? templates.filter(t => t.packages && t.packages.includes(data.package!))
    : templates;

  return filteredTemplates.map(t => ({
    name: processShortcodes(t.name, data),
    notes: processShortcodes(t.notes, data),
  }));
};
